import { useEffect } from 'react'

import { useMe } from 'src/hooks/useMe'
import { impersonatorTokenKey } from 'src/utils/impersonator'

import { isThemeMode, setThemeMode, themeModeVar } from './mode'

/**
 * PRODUCT-154 / PRO-547 — reconciles the local theme with the backend
 * preference (`theme_preference`, the cross-device source of truth) for every
 * platform user. Mounted post-auth in the MainRouter; renders nothing.
 *
 * Guard: impersonation — while a staff operator impersonates a customer, `me`
 * is the customer, so NEVER adopt their preference: the operator keeps their
 * own local theme, and the customer's stored pref is neither read nor written.
 *
 * localStorage already won first paint (boot script + `mode.ts` init). Here we
 * adopt the backend value only when it actually differs, so a local change the
 * user just made (which also wrote through to the backend) never round-trips.
 *
 * A RETIRED OR UNKNOWN BACKEND VALUE IS IGNORED, NOT ADOPTED AND NOT REWRITTEN.
 * `system` was a real stored preference until the app stopped following the OS
 * (see `./mode`), so it is still sitting in `theme_preference` for the users who
 * picked it. `isThemeMode` no longer accepts it, so the guard below skips
 * adoption entirely and the local mode stands — which for those users is the
 * `light` their own localStorage `system` already coerced to. Skipping rather
 * than coercing is the deliberate half: an explicit local Dark must not be
 * clobbered by a value the app can no longer interpret, and nothing here writes
 * to the backend, so the stored value is left for the user's next real choice
 * to replace.
 */
export const ThemeSync = (): null => {
  const me = useMe()
  const bePref =
    typeof me?.themePreference === 'string'
      ? me.themePreference.toLowerCase()
      : undefined

  useEffect(() => {
    if (localStorage.getItem(impersonatorTokenKey)) return
    if (isThemeMode(bePref) && bePref !== themeModeVar()) {
      setThemeMode(bePref)
    }
  }, [bePref])

  return null
}

export default ThemeSync
